<template>
  <div class="d-print-none" style="position: relative; z-index: 6">
    <div class="fixedtoolbar">
      <v-toolbar
        :flat="authenticated"
        color="primary"
        class="transparent-border-b"
        dark
        dense
        style="z-index: 100"
      >
        <div class="toolbarcontent">
          <v-toolbar-items style="width: 100%; align-items: center">
            <AppEquastrianLogo />
            <div
              v-if="canAccessBackoffice"
              data-test="backoffice"
              class="justify-left mx-1 hidden-sm-and-down"
            >
              <v-icon
                color="white"
                class="ma-auto mx-2"
                style="cursor: pointer"
                @click.native="() => (adminSearch = true)"
                >$iconSearch</v-icon
              >

              <v-menu offset-y light>
                <template #activator="{ on, attrs }">
                  <v-btn
                    color="edit"
                    style="border-radius: 2px; color: white; font-weight: bold"
                    class="mx-2"
                    min-width="90px"
                    v-bind="attrs"
                    v-on="on"
                  >
                    {{ $t("navigation:backoffice.menuLinkName", "Backoffice") }}
                    <v-icon small class="ml-2">$iconCircleCaretDown</v-icon>
                  </v-btn>
                </template>

                <v-list dense nav>
                  <v-list-item
                    v-for="(item, i) in privateMenuItems"
                    :key="i"
                    active-class="secondary"
                    dense
                  >
                    <v-list-item-content class="py-0">
                      <v-btn
                        :to="{ name: item.routeName }"
                        text
                        class="normaltext"
                        style="border-radius: 0px"
                        >{{ $t(item.linkTranslationKey, item.linkName) }}</v-btn
                      >
                    </v-list-item-content>
                  </v-list-item>
                </v-list>
              </v-menu>
            </div>

            <v-spacer />
            <div class="d-flex" style="align-items: center">
              <div class="hidden-sm-and-down">
                <AppLanguageBar class="hidden-sm-and-down" />
              </div>
              <v-btn
                v-if="
                  shoppingBasketAccess && isFeatureFlagSet('useOnlinePayments')
                "
                class="mr-3"
                :to="shoppingBasketAccess"
                small
                icon
              >
                <v-badge
                  color="accent"
                  :content="inBasket"
                  :value="inBasket"
                  tile
                  class="smaller-badge-cart"
                  overlap
                >
                  <v-icon small>$iconCart</v-icon>
                </v-badge>
              </v-btn>

              <div
                v-if="!authenticated"
                style="position: relative"
                class="d-flex"
                :class="{ 'mr-2': $vuetify.breakpoint.smAndDown }"
              >
                <v-btn
                  v-if="$vuetify.breakpoint.mdAndUp"
                  class="mx-2"
                  min-width="90px"
                  data-test="joinFederation"
                  text
                  @click="() => joinFederation()"
                >
                  <v-icon left>$iconRightToBracket</v-icon>
                  {{
                    $t(
                      "navigation:form.button.join_kipa",
                      `Join ${$federation.webTitle}`
                    )
                  }}
                </v-btn>
              </div>

              <div id="login-button">
                <v-btn
                  v-if="authenticated"
                  v-bind="
                    $vuetify.breakpoint.mdAndUp
                      ? { text: true }
                      : { icon: true, small: true }
                  "
                  @click="() => (userPanel = !userPanel)"
                >
                  <template v-if="$vuetify.breakpoint.mdAndUp">
                    <span
                      ref="userName"
                      :key="userNameForceUpdate"
                      class="user-name"
                    >
                      {{ $auth.userName }}
                      <span class="clip" />
                    </span>
                    <v-icon
                      small
                      right
                      :style="userPanel ? 'transform: rotate(180deg)' : ''"
                    >
                      $iconCircleCaretDown
                    </v-icon>
                  </template>
                  <template v-else>
                    <v-icon small>$iconUser</v-icon>
                  </template>
                </v-btn>

                <v-btn
                  v-else
                  data-test="loginUser"
                  min-width="90px"
                  :small="$vuetify.breakpoint.smAndDown"
                  color="accent"
                  @click="() => (userPanel = !userPanel)"
                >
                  <v-icon small left>$iconUser</v-icon>
                  {{ $t("navigation:signIn", "Login") }}
                </v-btn>

                <v-btn
                  class="hidden-md-and-up"
                  icon
                  small
                  aria-label="Menu"
                  data-test="mobileMainMenu"
                  @click="() => (mobileMenu = !mobileMenu)"
                >
                  <v-icon small>
                    {{ mobileMenu ? "$iconTimes" : "$iconBars" }}
                  </v-icon>
                </v-btn>
              </div>
            </div>
          </v-toolbar-items>
        </div>
      </v-toolbar>
    </div>
    <div class="fixedtoolbar-heightbox" />

    <v-card v-if="websiteStateType.name" class="dev-notice-box" rounded="0">
      <strong class="white--text"
        ><span>🚧</span>
        {{ websiteStateType.name }}
        <span>🚧</span></strong
      >
    </v-card>

    <AppHeroBanner v-if="!authenticated" id="jumpHeader" />
    <v-toolbar
      id="primaryToolbar"
      color="tertiary"
      height="50"
      dense
      class="transparent-border-t hidden-sm-and-down"
      style="z-index: 1; border-bottom: 1px solid transparent"
    >
      <nav class="toolbarcontent">
        <v-toolbar-items
          class="entsys--btn"
          style="text-transform: normal !important"
        >
          <v-btn
            v-for="(item, i) in allowedPublicMenuItems"
            :key="i"
            :to="{ name: item.routeName, params: item.params }"
            :class="['larger-text', 'mainMenuText--text']"
            active-class="mainMenuTextActive--text"
            text
            >{{ $t(item.linkTranslationKey, item.linkName) }}</v-btn
          >
        </v-toolbar-items>

        <v-spacer v-if="$vuetify.breakpoint.mdAndUp" />
        <template v-if="$federation.isFinlandEquestrian">
          <v-toolbar-items
            class="entsys--btn"
            style="text-transform: normal !important"
          >
            <v-btn
              text
              href="https://www.ratsastus.fi/"
              :class="['larger-text', 'mainMenuText--text']"
            >
              {{
                $t("navigation:menu.federationHomepage", "Federation Homepage")
              }}
              <v-icon right>$iconExternalLink</v-icon>
            </v-btn>
          </v-toolbar-items>
        </template>
        <template v-else-if="$federation.isBelgiumEquestrian">
          <v-toolbar-items
            class="entsys--btn"
            style="text-transform: normal !important"
          >
            <v-btn
              text
              :href="belgiumWebsite"
              :class="['larger-text', 'mainMenuText--text']"
            >
              {{
                $t("navigation:menu.federationHomepage", "Federation Homepage")
              }}
              <v-icon right>$iconExternalLink</v-icon>
            </v-btn>
          </v-toolbar-items>
        </template>
        <template v-else-if="$federation.isNorwayEquestrian">
          <v-toolbar-items
            class="entsys--btn"
            style="text-transform: normal !important"
          >
            <v-btn
              text
              :href="$federation.links.rulesPage"
              :class="['larger-text', 'mainMenuText--text']"
            >
              {{ $t("navigation:menu.rules", "Rules") }}
              <v-icon right>$iconExternalLink</v-icon>
            </v-btn>
          </v-toolbar-items>
        </template>
        <template v-if="$federation.isNetherlandsEquestrian">
          <v-toolbar-items
            class="entsys--btn"
            style="text-transform: normal !important"
          >
            <v-btn
              text
              href="https://knhs.nl/"
              :class="['larger-text', 'mainMenuText--text']"
            >
              {{
                $t("navigation:menu.federationHomepage", "Federation Homepage")
              }}
              <v-icon right>$iconExternalLink</v-icon>
            </v-btn>
          </v-toolbar-items>
        </template>
      </nav>
    </v-toolbar>

    <v-fade-transition>
      <div v-show="mobileMenu" class="mobile-main-menu hidden-md-and-up">
        <v-sheet
          tile
          color="tertiary"
          class="mobile-main-menu-wrapper py-2"
          style="z-index: 600"
        >
          <div>
            <div class="px-3">
              <AppLanguageBar right />
              <div v-if="!authenticated" class="d-flex justify-end mt-2">
                <v-btn
                  text
                  :class="['larger-text', buttonTextColor(true)]"
                  min-width="90px"
                  data-test="joinFederation"
                  @click="() => joinFederation()"
                >
                  <v-icon left class="join-fipa">$iconRightToBracket</v-icon>
                  <b style="font-size: 15px">{{
                    $t(
                      "navigation:form.button.join_kipa",
                      `Join ${$federation.webTitle}`
                    )
                  }}</b>
                </v-btn>
              </div>
            </div>
            <v-list
              v-if="canAccessBackoffice"
              data-test="backoffice"
              color="tertiary"
            >
              <v-list-group
                v-model="backofficeMenu"
                no-action
                eager
                color="white"
                class="backoffice-group-list"
                active-class="white mainMenuTextActive--text"
                :ripple="false"
                append-icon=""
              >
                <template #activator>
                  <v-list-item-content>
                    <v-list-item-title class="backoffice-group">
                      <strong>{{
                        $t("navigation:backoffice.menuLinkName", "Backoffice")
                      }}</strong>
                      <v-icon
                        :style="
                          backofficeMenu ? 'transform: rotate(180deg)' : ''
                        "
                      >
                        $iconCircleCaretDown
                      </v-icon>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
                <v-list-item
                  :class="['larger-text  white--text']"
                  color="white"
                  active-class="white--text"
                  @click="() => (adminSearch = true)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <div
                        class="d-flex justify-space-between"
                        style="align-items: center"
                      >
                        <div><b>Admin Search</b></div>
                        <div>
                          <v-icon>$iconSearch</v-icon>
                        </div>
                      </div>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-list-item
                  v-for="(item, i) in privateMenuItems"
                  :key="`${i}-admin`"
                  :to="{ name: item.routeName }"
                  :class="['larger-text  white--text']"
                  color="white"
                  active-class="white--text"
                  @click="() => (mobileMenu = false)"
                >
                  <v-list-item-content>
                    <v-list-item-title>
                      <b>{{ $t(item.linkTranslationKey, item.linkName) }}</b>
                    </v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
              </v-list-group>
            </v-list>

            <v-list color="tertiary" :ripple="false">
              <v-list-item
                v-for="(item, i) in allowedPublicMenuItems"
                :key="i"
                :to="{ name: item.routeName, params: item.params }"
                :class="['larger-text ']"
                color="white"
                active-class="white mainMenuTextActive--text"
                @click="() => (mobileMenu = false)"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <b>{{ $t(item.linkTranslationKey, item.linkName) }}</b>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div class="mobile-main-menu-footer">
            <v-list
              v-if="
                $federation.isFinlandEquestrian ||
                $federation.isNorwayEquestrian ||
                $federation.isBelgiumEquestrian ||
                $federation.isNetherlandsEquestrian
              "
              :ripple="false"
              color="tertiary"
            >
              <v-list-item
                v-if="$federation.isFinlandEquestrian"
                :href="$federation.links.homepage"
                :class="['larger-text ']"
                color="white"
                active-class="white mainMenuTextActive--text"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{
                      $t(
                        "navigation:menu.federationHomepage",
                        "Federation Homepage"
                      )
                    }}</strong>
                    <v-icon right>$iconExternalLink</v-icon>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-if="$federation.isBelgiumEquestrian"
                :href="belgiumWebsite"
                :class="['larger-text ']"
                color="white"
                active-class="white mainMenuTextActive--text"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{
                      $t(
                        "navigation:menu.federationHomepage",
                        "Federation Homepage"
                      )
                    }}</strong>
                    <v-icon right>$iconExternalLink</v-icon>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-else-if="$federation.isNorwayEquestrian"
                :href="$federation.links.rulesPage"
                :class="['larger-text ']"
                color="white"
                active-class="white mainMenuTextActive--text"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <strong> {{ $t("navigation:menu.rules", "Rules") }}</strong>
                    <v-icon right>$iconExternalLink</v-icon>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
              <v-list-item
                v-else-if="$federation.isNetherlandsEquestrian"
                href="https://knhs.nl/"
                :class="['larger-text ']"
                color="white"
                active-class="white mainMenuTextActive--text"
              >
                <v-list-item-content>
                  <v-list-item-title>
                    <strong>{{
                      $t(
                        "navigation:menu.federationHomepage",
                        "Federation Homepage"
                      )
                    }}</strong>
                    <v-icon right>$iconExternalLink</v-icon>
                  </v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
        </v-sheet>
      </div>
    </v-fade-transition>

    <AdminSearch v-if="canAccessBackoffice" v-model="adminSearch" />

    <UserPanel :key="userNameForceUpdate" :model-value.sync="userPanel" />
  </div>
</template>

<script>
import { ref, onUnmounted, nextTick } from "vue";
import gql from "fraql";
import i18next from "i18next";
import { getFederationConfiguration } from "@composables/masterdata/config";

import AppHeroBanner from "@elements/banners/AppHeroBanner.vue";
import AppLanguageBar from "@/components/elements/AppLanguageBar.vue";
import AppEquastrianLogo from "@/components/elements/AppEquastrianLogo.vue";
import UserPanel from "./UserPanel.vue";
import AdminSearch from "@/components/elements/AdminSearch/AdminSearch.vue";

import FipaListener from "@/utils/listenerUtility";

export default {
  name: "AppNavigation",
  components: {
    AdminSearch,
    AppLanguageBar,
    AppEquastrianLogo,
    AppHeroBanner,
    UserPanel
  },
  setup() {
    const userNameForceUpdate = ref(false);
    FipaListener.on("nameChange", forceNameUpdate);

    function forceNameUpdate() {
      nextTick(() => {
        userNameForceUpdate.value = !userNameForceUpdate.value;
      });
    }
    onUnmounted(() => {
      FipaListener.off("nameChange", forceNameUpdate);
    });

    return {
      forceNameUpdate,
      userNameForceUpdate,
      ...getFederationConfiguration()
    };
  },
  data() {
    return {
      inBasket: 0,
      backofficeMenu: false,
      mobileMenuState: false,
      anonymouseBasketId: null,
      adminSearch: false,
      userPanel: false,
      currentLanguage: i18next.language
    };
  },
  computed: {
    belgiumWebsite() {
      switch (this.currentLanguage.toLowerCase()) {
        case "fr-be":
        case "fr":
          return "https://equibel.be/fr/";
        default:
          return "https://equibel.be/nl/";
      }
    },
    canAccessBackoffice() {
      return this.$auth.canAccessRoute("Backoffice");
    },
    mobileMenu: {
      get() {
        return this.mobileMenuState;
      },
      set(v) {
        this.mobileMenuState = v;
        if (v === true) {
          document.documentElement.classList.add("overflow-hidden");
        } else {
          document.documentElement.classList.remove("overflow-hidden");
        }
      }
    },
    publicMenuItems() {
      return [
        {
          routeName: "Calendar",
          linkTranslationKey: "navigation:menu.calendar",
          linkName: "Calendar"
        },
        {
          routeName: "NonCompetitionCalendar",
          linkTranslationKey: "navigation:menu.nonCompetitionCalendar",
          linkName: "Education"
        },
        {
          routeName: "PeopleSearchPage",
          linkTranslationKey: "navigation:menu.people",
          linkName: "People"
        },
        {
          routeName: "HorsesSearchPage",
          linkTranslationKey: "navigation:menu.horses",
          linkName: "Horses"
        },
        {
          routeName: "OrganisationsSearchPage",
          linkTranslationKey: "navigation:menu.organisations",
          linkName: "Organisations"
        },
        {
          routeName: "GroupsSearchPage",
          linkTranslationKey: "navigation:menu.groups",
          linkName: "Groups"
        },
        {
          routeName: "RankingTablesPage",
          linkTranslationKey: "navigation:menu.rankings",
          linkName: "Rankings"
        },
        {
          routeName: "CompetingDifficultyLevelsPage",
          linkTranslationKey: "navigation:competingDifficultyLevels.menu",
          linkName: "Levels"
        },
        {
          routeName: "History",
          linkTranslationKey: "navigation:menu.history",
          linkName: "History"
        },
        {
          routeName: "Shop",
          linkTranslationKey: "navigation:menu.shop",
          linkName: "Shop"
        },
        {
          routeName: "Support",
          linkTranslationKey: "navigation:menu.support",
          linkName: "Support"
        }
      ];
    },
    privateMenuItems() {
      return [
        {
          routeName: "MasterDataManagementPage",
          linkTranslationKey: "navigation:menu.masterdata",
          linkName: "Master data",
          hasAccess: this.$auth.canAccessRoute("MasterDataManagementPage")
        },
        {
          routeName: "SanctionsManagement",
          linkTranslationKey: "navigation:menu.sanctions",
          linkName: "Sanctions",
          hasAccess:
            this.$auth.checkUserCanPerformMutationsInScopeAny("sanctionCreate")
        },
        {
          routeName: "ReportsViewer",
          linkTranslationKey: "navigation:backoffice.reports.menu",
          linkName: "Reports",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("ReportDefinition")
        },

        {
          routeName: "AdminAuditLog",
          linkTranslationKey: "navigation:menu.auditlogs",
          linkName: "Audit logs",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("AuditEvent")
        },
        {
          routeName: "SalesPage",
          linkTranslationKey: "navigation:menu.sales",
          linkName: "Sales",
          hasAccess: this.$auth.canAccessRoute("BackofficeSales")
        },

        {
          routeName: "InvoicesManagement",
          linkTranslationKey: "navigation:menu.invoices",
          linkName: "Invoices",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("Invoice")
        },
        {
          routeName: "AdminPayments",
          linkTranslationKey: "navigation:menu.adminpayments",
          linkName: "Payments",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("Payment")
        },
        {
          routeName: "AdminPayouts",
          linkTranslationKey: "navigation:menu.adminpayouts",
          linkName: "Payouts",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("Payout")
        },

        {
          routeName: "PersonDebtBalances",
          linkTranslationKey: "navigation:menu.debtBalances",
          linkName: "Users´ debt balances",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("PersonDebtBalance")
        },

        {
          routeName: "TaxInformations",
          linkTranslationKey: "navigation:menu.taxInformationApproval",
          linkName: "Tax information approval",
          hasAccess: this.$auth.checkUserCanPerformMutationsInScopeAny(
            "contactIncomeTaxInfoMarkReviewed"
          )
        },
        {
          routeName: "TaxOffice",
          linkTranslationKey: "navigation:menu.taxoffice",
          linkName: "Tax office",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("TaxOfficeReport")
        },

        {
          routeName: "PersonApplications",
          linkTranslationKey: "navigation:menu.personapplications",
          linkName: "Person applications",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("PersonApplication")
        },
        {
          routeName: "HorseApplications",
          linkTranslationKey: "navigation:menu.horsesapplications",
          linkName: "Horse applications",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("HorseApplication")
        },
        {
          routeName: "AdminOrganisations",
          linkTranslationKey: "navigation:menu.AdminOrganisations",
          linkName: "Organisations",
          hasAccess: this.$auth.checkUserCanPerformMutationsInScopeAny(
            "organisationCreateOrUpdate"
          )
        },
        {
          routeName: "ExternalResultReports",
          linkTranslationKey: "navigation:menu.externalResults",
          linkName: "ExternalResultReports",
          hasAccess: this.$auth.checkUserCanPerformMutationsInScopeAny(
            "extResApplicationAccept"
          )
        },
        {
          routeName: "OfficialDashboard",
          linkTranslationKey: "navigation:menu.officials",
          linkName: "OfficialDashboard",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny(
            "PersonProfQualification"
          )
        },
        {
          routeName: "PersonExamsManagement",
          linkTranslationKey: "navigation:menu.exams",
          linkName: "PersonExamsManagement",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("PersonExam")
        },
        {
          routeName: "PersonCertificateManagement",
          linkTranslationKey: "navigation:menu.certificates",
          linkName: "PersonCertificateManagement",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("PersonCertificate")
        },
        {
          routeName: "PersonDuplicate",
          linkTranslationKey: "navigation:menu.peopleDuplicates",
          linkName: "PersonDuplicate",
          hasAccess: this.$auth.canAccessEntitiesInScopeAny("PersonDuplicate")
        },
        {
          routeName: "Tehnical",
          linkTranslationKey: "navigation:menu.tehnical",
          linkName: "Tehnical",
          hasAccess: this.$auth.checkUserCanPerformMutationsInScopeAny(
            "simulateErrorResponse"
          )
        }
      ].filter(route => {
        if (route.hasAccess != undefined) return route.hasAccess;
        return true;
      });
    },
    authenticated() {
      return this.$auth && this.$auth.isAuthenticated;
    },
    shoppingBasketAccess() {
      if (this.authenticated) {
        return {
          name: "Payables",
          params: { personId: this.$auth.user.id }
        };
      } else if (sessionStorage.getItem("shoppingBasketId")) {
        return {
          name: "AnonymouseBasket",
          params: {
            shoppingBasketId: sessionStorage.getItem("shoppingBasketId")
          }
        };
      } else {
        return false;
      }
    },
    allowedPublicMenuItems() {
      return this.publicMenuItems.filter(i =>
        this.$auth.canAccessRoute(i.routeName)
      );
    },
    websiteStateType() {
      const object = {};
      switch (process.env.NODE_ENV) {
        case "development":
          object.name = "Development";
          break;
      }
      return object;
    }
  },
  watch: {
    $route() {
      this.userPanel = false;
      this.mobileMenu = false;
    },
    "$vuetify.breakpoint.mdAndUp"(v) {
      if (v == true) this.mobileMenu = false;
    },
    mobileMenu(v) {
      if (v === false) {
        const beacon = document.querySelector("#beacon-container");
        if (beacon) {
          beacon.style.display = "initial";
        }
      } else if (v === true) {
        const beacon = document.querySelector("#beacon-container");
        if (beacon) {
          beacon.style.display = "none";
        }
      }
    }
  },
  apollo: {
    basket: {
      query: gql`
        query personBasketInterval($id: ID) {
          person(id: $id) {
            id
            payables {
              id
              payableSections {
                id
                payableItems {
                  id
                }
              }
            }
          }
        }
      `,
      manual: true,
      skip() {
        return !this.$auth.user.id;
      },
      variables() {
        return {
          id: this.$auth.user.id
        };
      },
      result({ loading, data }) {
        if (loading || !data) return;
        this.inBasket = 0;

        (data.person.payables || []).forEach(payable =>
          payable.payableSections.forEach(
            section => (this.inBasket += section.payableItems.length)
          )
        );

        if (this.inBasket > 0) this.$apollo.queries.basket.startPolling(300000);
        else this.$apollo.queries.basket.stopPolling();
      }
    },
    anonymouseBasket: {
      query: gql`
        query shoppingBasketInterval($id: ID) {
          shoppingBasket(id: $id) {
            id
            payableGroups {
              id
              payableSections {
                id
                payableItems {
                  id
                }
              }
            }
          }
        }
      `,
      manual: true,
      skip() {
        return !!this.$auth.user.id || !this.anonymouseBasketId;
      },
      variables() {
        return {
          id: this.anonymouseBasketId
        };
      },
      result({ loading, data }) {
        if (loading || !data) return;
        this.inBasket = 0;

        (data.shoppingBasket.payableGroups || []).forEach(payable =>
          payable.payableSections.forEach(
            section => (this.inBasket += section.payableItems.length)
          )
        );
        if (this.inBasket > 0)
          this.$apollo.queries.anonymouseBasket.startPolling(300000);
        else this.$apollo.queries.anonymouseBasket.stopPolling();
      }
    }
  },
  beforeCreate() {
    const cssRoot = getComputedStyle(document.documentElement);
    const primary = cssRoot.getPropertyValue("--v-primary-base");
    document.documentElement.style.setProperty(
      "--v-primary-opacity",
      primary + "ab"
    );
  },
  created() {
    FipaListener.on("emitRefreshBasket", this.refreshBasket, {
      passive: true
    });

    i18next.on("languageChanged", this.setLanguage);
    document.addEventListener("visibilitychange", this.handlePollingBasket);
  },
  beforeDestroy() {
    FipaListener.off("emitRefreshBasket", this.refreshBasket);

    i18next.off("languageChanged", this.setLanguage);
    document.removeEventListener("visibilitychange", this.handlePollingBasket);
  },
  methods: {
    setLanguage(lng) {
      this.currentLanguage = lng;
    },
    refreshBasket() {
      this.anonymouseBasketId = sessionStorage.getItem("shoppingBasketId");
      this.$apollo.queries.basket.refetch();
      this.$apollo.queries.anonymouseBasket.refetch();
    },
    handlePollingBasket() {
      switch (document.visibilityState) {
        case "hidden":
          this.$apollo.queries.basket.stopPolling();
          this.$apollo.queries.anonymouseBasket.stopPolling();
          break;
        case "visible":
          this.$apollo.queries.basket.refetch();
          this.$apollo.queries.anonymouseBasket.refetch();
          break;
      }
    },

    buttonTextColor(justColorText) {
      switch (this.$federation.countryCode) {
        case "FI":
          return `${
            !justColorText ? "fipa-active-color-applier" : ""
          } biscay--text`;
        case "NO":
          return `${
            !justColorText ? "fipa-active-color-applier" : ""
          } white--text`;
        default:
          return "";
      }
    },
    joinFederation() {
      if (this.$auth.checkUserCanPerformMutationsInScopeAny("personSignup")) {
        this.$router.push({ name: "PersonSelfSignup", hash: "" });
        return;
      }
      if (this.$route.name == "JoinFederation") return;
      this.$router.push({ name: "JoinFederation" });
    }
  }
};
</script>
<style lang="scss">
.smaller-badge-cart .v-badge__badge {
  min-width: 15px !important;
  height: 15px;
  transform: translate(2px, 0px) !important;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 11px;
  border-radius: 2px !important;
}
@media (max-width: 960px) {
  .backoffice-group-list {
    .v-list-group__header {
      padding: 0 8px !important;
    }
  }
}
</style>
<style lang="scss" scoped>
@use "../css/mobile-menu-list-custom.scss";
.fipa-active-color-applier {
  transition: 0.3s cubic-bezier(0.25, 0.8, 0.5, 1);

  &.v-btn--active,
  &:hover {
    color: var(--v-mainMenuTextActive-base) !important;
  }
}

.user-name {
  white-space: nowrap;
  text-overflow: clip;
  overflow: hidden;
  position: relative;
  @media (max-width: 410px) {
    max-width: 25vw;
    .clip {
      position: absolute;
      top: -2px;
      right: -13px;
      height: 21px;
      width: 25px;
      background-image: linear-gradient(
        to right,
        var(--v-primary-opacity) 8%,
        var(--v-primary-base) 77%
      );
    }
  }
}

.mobile-main-menu {
  position: fixed;
  top: 48px;
  z-index: 50;
  &-wrapper {
    // - 48px since top bar takes 48px of spacv
    // Here it covers screen's height if not enough elements
    height: calc(100vh - 48px);
    width: 100vw;
    overflow-y: auto;
    padding: 1em 1.2em;
  }

  &-footer {
    // HelpScout is annoying
    margin-bottom: 42px;
    margin-top: 42px;
  }

  @include mobile-menu-list-custom.custom-list-style(
    mainMenuText,
    mainMenuTextActive,
    white
  );

  .backoffice-header {
    padding-left: 1.2em;
    padding-right: 1.2em;
  }
}

.mobile-website-state {
  position: absolute;
  top: 48px;
  left: 16px;
}

@media (max-width: 960px) {
  .toolbarcontent {
    padding: 0px;
  }
  .backoffice-group {
    display: flex;
    align-items: center;
    justify-content: space-between;
  }
  .fixedtoolbar {
    position: fixed !important;
    padding: 0 !important;
    top: 0;
    width: 100%;
    z-index: 1050;
  }
  .fixedtoolbar-heightbox {
    display: block;
    height: 48px;
  }
}
.admin-mobile > .activator-admin-mobile {
  color: white;
  align-self: center;
  display: flex;
  justify-content: center;
}

.dev-notice-box {
  padding: 6px;
  text-align: center;
  background-color: #570000;
  strong {
    font-size: 18px;
    span {
      font-size: 21px;
      margin: 0 12px;
    }
    @media (max-width: 960px) {
      font-size: 14px;

      span {
        font-size: 16px;
        margin: 0 12px;
      }
    }
  }
  @media (max-width: 960px) {
    width: 100%;
    padding: 2px;
  }
}
</style>
